import { Search } from 'lucide-react'

import { useState } from 'react'

import { Input, PosButton, Segmented, StockBadge } from '@/components/ui'
import { PageHeader } from '@/components/pos/PageHeader'
import { ExpiryList } from '@/components/pos/inventory/ExpiryList'
import { StockMove, StockMoveDialog } from '@/components/pos/inventory/StockMoveDialog'
import { Suppliers } from '@/components/pos/inventory/Suppliers'
import { useSession } from '@/contexts/SessionContext'
import { useCategories, useProducts } from '@/hooks/features/catalog'
import { useDebounce } from '@/hooks/ui/useDebounce'
import { cn } from '@/lib/utils'
import { formatMoney } from '@/utils'

type Filter = 'all' | 'low' | 'out'
type View = 'stock' | 'expiry' | 'suppliers'

/**
 * Stock for the shop floor ("do we have any more of these?", the low-stock list) and the stock room's work (B20):
 * stock in and transfers out are online; adjustments too, except with one branch and one terminal.
 */
export const InventoryPage = () => {
  const { stage, can } = useSession()
  const real = stage !== 'demo'
  const views = (
    [
      ['stock', 'Stock', true],
      ['expiry', 'Expiry', real && can('stocks:expiry')],
      ['suppliers', 'Suppliers', real && can('suppliers:list')],
    ] as const
  ).filter(([, , ok]) => ok)
  const moves = (
    [
      ['purchase', 'Stock in', 'stocks:purchase'],
      ['adjust', 'Adjust', 'stocks:adjust'],
      ['transfer', 'Transfer out', 'stocks:transfer'],
    ] as const
  ).filter(([, , key]) => real && can(key))
  const [view, setView] = useState<View>('stock')
  const [move, setMove] = useState<StockMove | null>(null)
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState<Filter>('all')
  const debounced = useDebounce(search)
  const { data: categories } = useCategories()
  const { data, isLoading } = useProducts({ search: debounced || undefined, low_stock: filter !== 'all' || undefined })
  const items = (data?.items ?? []).filter((p) => filter !== 'out' || p.stock <= 0)
  const all = data?.items ?? []

  return (
    <div className="flex h-full flex-col">
      <PageHeader title="Stock" subtitle="Live counts for this outlet. Sales and refunds update them immediately.">
        {moves.map(([key, label]) => (
          <PosButton key={key} variant="outline" onClick={() => setMove(key)}>
            {label}
          </PosButton>
        ))}
        {view === 'stock' && (
          <div className="relative w-full sm:w-72">
            <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Name, SKU or barcode" onCameraScan={setSearch} className="pl-9" />
          </div>
        )}
      </PageHeader>
      <StockMoveDialog move={move} onClose={() => setMove(null)} />
      {views.length > 1 && (
        <div className="px-4 pt-3 md:px-6">
          <Segmented<View> value={view} onChange={setView} options={views.map(([value, label]) => ({ value, label }))} />
        </div>
      )}
      {view === 'expiry' && <div className="flex min-h-0 flex-1 flex-col pt-3"><ExpiryList /></div>}
      {view === 'suppliers' && <div className="flex min-h-0 flex-1 flex-col pt-3"><Suppliers /></div>}
      {view === 'stock' && (
      <>
      <div className="flex items-center gap-3 px-4 py-3 md:px-6">
        <Segmented<Filter>
          value={filter}
          onChange={setFilter}
          options={[
            { value: 'all', label: 'All' },
            { value: 'low', label: 'Low stock' },
            { value: 'out', label: 'Out of stock' },
          ]}
        />
        {filter === 'all' && <span className="text-xs text-muted-foreground">{all.length} products</span>}
      </div>
      <div className="min-h-0 flex-1 overflow-auto px-4 pb-4 md:px-6">
        <ul className={cn('space-y-2 sm:hidden', isLoading && 'opacity-60')}>
          {items.map((p) => (
            <li key={p._id} className="rounded-md border border-border bg-card p-3 text-sm shadow-z1">
              <div className="flex items-start justify-between gap-3">
                <span className="font-medium text-ink">{p.name}</span>
                <span className="shrink-0 tabular-nums text-ink">{formatMoney(p.price)}</span>
              </div>
              <div className="mt-1.5 flex items-center justify-between gap-3">
                <span className="truncate font-mono text-xs text-text-2">{p.sku}</span>
                <StockBadge product={p} />
              </div>
            </li>
          ))}
          {!isLoading && items.length === 0 && <li className="py-10 text-center text-muted-foreground">Nothing here.</li>}
        </ul>
        <div className="hidden overflow-hidden rounded-md border border-border bg-card shadow-z1 sm:block">
          <table className="w-full text-sm">
            <thead className="bg-subdued text-left text-xs text-muted-foreground">
              <tr className="[&>th]:h-9 [&>th]:border-b [&>th]:border-border [&>th]:px-4 [&>th]:font-medium">
                <th>Product</th>
                <th className="hidden sm:table-cell">SKU</th>
                <th className="hidden md:table-cell">Category</th>
                <th className="text-right">Price</th>
                <th className="w-48">Stock</th>
              </tr>
            </thead>
            <tbody className={cn(isLoading && 'opacity-60')}>
              {items.map((p) => {
                const pct = Math.min(100, (p.stock / Math.max(p.low_stock_threshold * 5, 1)) * 100)
                const low = p.stock <= p.low_stock_threshold
                return (
                  <tr key={p._id} className="border-b border-border-subtle last:border-0 [&>td]:h-12 [&>td]:px-4">
                    <td className="font-medium text-ink">{p.name}</td>
                    <td className="hidden font-mono text-xs text-text-2 sm:table-cell">{p.sku}</td>
                    <td className="hidden text-text-2 md:table-cell">{categories?.find((c) => c._id === p.category_id)?.name}</td>
                    <td className="text-right tabular-nums text-ink">{formatMoney(p.price)}</td>
                    <td>
                      <div className="flex items-center gap-3">
                        <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-fill">
                          <div className={cn('h-full rounded-full', p.stock <= 0 ? 'bg-error' : low ? 'bg-warning' : 'bg-success')} style={{ width: `${pct}%` }} />
                        </div>
                        <StockBadge product={p} />
                      </div>
                    </td>
                  </tr>
                )
              })}
              {!isLoading && items.length === 0 && (
                <tr>
                  <td colSpan={5} className="py-10 text-center text-muted-foreground">
                    Nothing here.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      </>
      )}
    </div>
  )
}
